/**
 * Backup link: everything WhoSteps keeps in this browser (progress, streak, Knowledge Map facts)
 * packed into a link. Opening it on any device merges it in — nothing is ever lost by restoring,
 * because every part merges (more rounds played wins, known beats learned, played dates are unioned).
 * The data travels in the URL fragment (#restore=…), which browsers never send to the server.
 * Safari deletes site storage after about a week without a visit; this is the way back.
 */
import { type Knowledge } from "./knowledge";
import { mergePlayed } from "./streak";

import { PREFIX } from "./storage";
export { PREFIX };
/** Keys that are per-device settings or transient, never backed up. */
const SKIP = new Set(["level", "challenge"]);

export type State = Record<string, unknown>;

export function exportState(ls: Storage = localStorage): State {
  const out: State = {};
  try {
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (!k?.startsWith(PREFIX)) continue;
      const key = k.slice(PREFIX.length);
      if (SKIP.has(key)) continue;
      try {
        out[key] = JSON.parse(ls.getItem(k) ?? "null");
      } catch {
        /* skip unreadable */
      }
    }
  } catch {
    /* storage blocked */
  }
  return out;
}

const played = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const rounds = (v: unknown): number => {
  const r = (v as { results?: unknown } | null)?.results;
  return Array.isArray(r) ? r.length : -1;
};

export function mergeKnowledge(a: Knowledge, b: Knowledge): Knowledge {
  const out: Knowledge = { ...a };
  for (const [id, f] of Object.entries(b ?? {})) {
    if (!f || (f.t !== "k" && f.t !== "l") || typeof f.d !== "string") continue;
    const p = out[id];
    if (!p) out[id] = { t: f.t, d: f.d };
    else out[id] = { t: p.t === "k" || f.t === "k" ? "k" : "l", d: p.d < f.d ? p.d : f.d };
  }
  return out;
}

/** Merge a backup into the current state. Pure. */
export function mergeState(cur: State, inc: State): State {
  const out: State = { ...cur };
  for (const [key, val] of Object.entries(inc)) {
    if (SKIP.has(key)) continue;
    if (key === "facts") out.facts = mergeKnowledge((cur.facts as Knowledge) ?? {}, (val as Knowledge) ?? {});
    else if (key === "played") out.played = mergePlayed(played(cur.played), played(val));
    else if (key.startsWith("progress:")) {
      if (rounds(val) > rounds(cur[key])) out[key] = val;
    }
  }
  return out;
}

export function writeState(s: State, ls: Storage = localStorage): void {
  for (const [k, v] of Object.entries(s)) {
    try {
      ls.setItem(PREFIX + k, JSON.stringify(v));
    } catch {
      /* quota / blocked */
    }
  }
}

// ---------- encoding: JSON → deflate (when the browser can) → base64url ----------
const b64url = (bytes: Uint8Array): string => {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
const unb64url = (s: string): Uint8Array => {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

async function pipe(bytes: Uint8Array, stream: GenericTransformStream): Promise<Uint8Array> {
  const res = new Response(new Blob([bytes as BlobPart]).stream().pipeThrough(stream as unknown as ReadableWritablePair<Uint8Array, Uint8Array>));
  return new Uint8Array(await res.arrayBuffer());
}

/**
 * "2" + deflated, or "3" + plain JSON where CompressionStream is missing. "1"/"0" were the same before the restart
 * (DECISIONS 2026-10-03 "Restart"); those backups hold progress for puzzles that no longer exist and are refused.
 */
export async function encodeBackup(s: State): Promise<string> {
  const raw = new TextEncoder().encode(JSON.stringify(s));
  if (typeof CompressionStream === "function") {
    try {
      return "2" + b64url(await pipe(raw, new CompressionStream("deflate-raw")));
    } catch {
      /* fall through */
    }
  }
  return "3" + b64url(raw);
}

/** A backup link from before the restart. */
export const isOldBackup = (code: string): boolean => code[0] === "0" || code[0] === "1";

export async function decodeBackup(code: string): Promise<State | null> {
  try {
    const kind = code[0];
    let bytes = unb64url(code.slice(1));
    if (kind === "2") {
      if (typeof DecompressionStream !== "function") return null;
      bytes = await pipe(bytes, new DecompressionStream("deflate-raw"));
    } else if (kind !== "3") return null;
    const v: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return v && typeof v === "object" && !Array.isArray(v) ? (v as State) : null;
  } catch {
    return null;
  }
}
